import { apiRequest, ApiError } from '../../api/client'
import {
  listOps,
  putOp,
  deleteOp,
  getPhoto,
  deletePhoto,
  countOutbox,
  resetFailedOps,
  purgeSubmission,
  getRun,
  saveRun,
} from './db'
import type { OutboxOp } from './db'
import { readyOps, applyRetry, applyFailure, classifyResponse } from './outbox'
import { clearLocalPhotoUrl } from './queueSink'

/**
 * ตัวส่งข้อมูลขึ้นเซิร์ฟเวอร์
 *
 * กฎที่ทำให้ข้อมูลภาคสนามไม่หายและไม่ซ้ำ:
 *   1. ลบรายการออกจากคิว "เมื่อเซิร์ฟเวอร์ตอบสำเร็จเท่านั้น"
 *   2. ทุก endpoint ที่เรียกเป็น idempotent (id มาจากฝั่ง client) ส่งซ้ำจึงไม่เกิดข้อมูลซ้ำ
 *   3. ส่งเรียงตามลำดับที่ผู้ใช้ทำจริง
 *   4. เจอ 401 → หยุดทั้งคิว ไม่ทิ้งข้อมูล รอผู้ใช้ล็อกอินใหม่
 */

export type SyncState = {
  pending: number
  /** รายการที่ล้มเหลวถาวรแล้ว (ข้อมูลไม่ผ่านตรวจ/ถูกปฏิเสธ) — เอ็นจินจะไม่ลองส่งซ้ำเองอีก ต้องกด "ลองส่งอีกครั้ง" */
  failed: number
  syncing: boolean
  online: boolean
  needsLogin: boolean
  lastError: string | null
  lastSyncedAt: number | null
}

type Listener = (state: SyncState) => void

let state: SyncState = {
  pending: 0,
  failed: 0,
  syncing: false,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  needsLogin: false,
  lastError: null,
  lastSyncedAt: null,
}

const listeners = new Set<Listener>()
let running = false
let timer: ReturnType<typeof setTimeout> | null = null
let started = false

function emit(patch: Partial<SyncState>): void {
  state = { ...state, ...patch }
  for (const listener of listeners) listener(state)
}

export function subscribeSync(listener: Listener): () => void {
  listeners.add(listener)
  listener(state)
  return () => listeners.delete(listener)
}

export function getSyncState(): SyncState {
  return state
}

async function sendOp(op: OutboxOp): Promise<{ status: number; message: string }> {
  try {
    switch (op.kind) {
      case 'createSubmission':
        await apiRequest('/api/submissions', {
          method: 'POST',
          body: { submissionId: op.submissionId, ...op.payload },
        })
        return { status: 200, message: '' }

      case 'step': {
        const stepKey = String(op.payload['stepKey'])
        await apiRequest(
          `/api/submissions/${op.submissionId}/steps/${encodeURIComponent(stepKey)}`,
          {
            method: 'PUT',
            body: {
              nodeId: op.payload['nodeId'],
              seq: op.payload['seq'],
              answers: op.payload['answers'],
              answeredAt: op.payload['answeredAt'],
            },
          },
        )
        return { status: 200, message: '' }
      }

      case 'photo': {
        const assetId = String(op.payload['assetId'])
        const photo = await getPhoto(assetId)
        if (!photo) {
          // ไฟล์หายไปแล้ว (เคยส่งสำเร็จ หรือถูกลบ) — ถือว่าจบรายการนี้
          return { status: 200, message: '' }
        }
        const form = new FormData()
        form.set('clientAssetId', assetId)
        form.set('submissionId', photo.submissionId)
        form.set('stepKey', photo.stepKey)
        form.set('fieldName', photo.fieldName)
        form.set('file', photo.blob, `${assetId}.jpg`)
        await apiRequest('/api/uploads/photo', { method: 'POST', formData: form })
        await deletePhoto(assetId)
        // เปลี่ยนจาก blob ในเครื่องไปใช้ /api/assets/ จริง + เลิกป้าย "รอส่ง"
        clearLocalPhotoUrl(assetId)
        return { status: 200, message: '' }
      }

      case 'complete':
        await apiRequest(`/api/submissions/${op.submissionId}/complete`, {
          method: 'POST',
          body: op.payload,
        })
        return { status: 200, message: '' }
    }
  } catch (err) {
    if (err instanceof ApiError) return { status: err.status, message: err.message }
    return { status: 0, message: err instanceof Error ? err.message : 'ส่งข้อมูลไม่สำเร็จ' }
  }
}

async function runOnce(): Promise<void> {
  if (running || state.needsLogin) return
  running = true
  emit({ syncing: true })

  try {
    const ops = readyOps(await listOps())
    let stopEarly = false

    for (const op of ops) {
      const result = await sendOp(op)
      const outcome = classifyResponse(result.status, result.message)

      if (outcome.type === 'done') {
        await deleteOp(op.opId)
        if (op.kind === 'complete') {
          const run = await getRun(op.submissionId)
          if (run) await saveRun({ ...run, status: 'completed', updatedAt: Date.now() })
        }
        continue
      }

      if (outcome.type === 'unauthorized') {
        emit({ needsLogin: true, lastError: 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่ (ข้อมูลของคุณยังอยู่ครบ)' })
        stopEarly = true
        break
      }

      if (outcome.type === 'gone') {
        // งานนี้ถูกลบไปแล้วที่ต้นทาง (เช่นแอดมินลบทิ้ง) — เคลียร์ข้อมูลในเครื่องของงานนี้ทั้งหมด
        // ไม่งั้นจะค้างเป็น "ทำค้างไว้"/"ส่งไม่สำเร็จถาวร" ตลอดไปทั้งที่ไม่มีอะไรให้ส่งแล้วจริง ๆ
        const { purgedAssetIds } = await purgeSubmission(op.submissionId)
        for (const assetId of purgedAssetIds) clearLocalPhotoUrl(assetId)
        continue
      }

      if (outcome.type === 'failed') {
        await putOp(applyFailure(op, outcome.message))
        emit({ lastError: `ส่งข้อมูลบางรายการไม่สำเร็จ: ${outcome.message}` })
        continue
      }

      // เน็ตล่ม/เซิร์ฟเวอร์ไม่พร้อม — หยุดรอบนี้แล้วค่อยลองใหม่ตามเวลาถอย
      await putOp(applyRetry(op, outcome.message))
      stopEarly = true
      break
    }

    const { pending, failed } = await countOutbox()
    emit({
      pending,
      failed,
      lastSyncedAt: stopEarly ? state.lastSyncedAt : Date.now(),
      ...(pending === 0 ? { lastError: null } : {}),
    })

    if (pending > 0) scheduleNextRun()
  } finally {
    running = false
    emit({ syncing: false })
  }
}

function scheduleNextRun(delayMs = 30_000): void {
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => {
    void runOnce()
  }, delayMs)
}

export function requestSync(): void {
  void runOnce()
}

/**
 * ให้หน้าจอที่ลบข้อมูลในเครื่องเองนอกรอบ sync ปกติ (เช่นเจอว่างานถูกลบจากหลังบ้านไปแล้ว
 * ตอนโหลดรายการ "ทำค้างไว้") เรียกให้ตัวเลขในแถบสถานะอัปเดตตามทันที ไม่ต้องรอรอบ sync ถัดไป
 */
export async function refreshSyncCounts(): Promise<void> {
  const { pending, failed } = await countOutbox()
  emit({ pending, failed })
}

/** เรียกครั้งเดียวตอนแอปเริ่ม — ผูก trigger ทั้งหมดของการ sync */
export function startSyncLoop(): void {
  if (started || typeof window === 'undefined') return
  started = true

  window.addEventListener('online', () => {
    emit({ online: true })
    requestSync()
  })
  window.addEventListener('offline', () => emit({ online: false }))
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') requestSync()
  })

  void countOutbox().then(({ pending, failed }) => {
    emit({ pending, failed })
    if (pending > 0) requestSync()
  })
}

/**
 * ให้โอกาสส่งใหม่กับรายการที่เคยล้มเหลวถาวรทั้งหมด (ปุ่ม "ลองส่งอีกครั้ง" ในหน้าจอ)
 * เผื่อกรณีที่ต้นเหตุ (เช่น bug ฝั่งเซิร์ฟเวอร์) ได้รับการแก้ไขแล้ว ไม่ต้องให้ข้อมูลค้างตายไปเฉย ๆ
 */
export function retryFailed(): void {
  void resetFailedOps().then(() => requestSync())
}

/** เรียกหลังผู้ใช้ล็อกอินใหม่ */
export function resumeAfterLogin(): void {
  emit({ needsLogin: false, lastError: null })
  requestSync()
}
